import type { State, DiagramLink, LinkRendererContext } from './Diagram.types';
import { getItemAriaLabel } from './utils/diagramUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { itemStyles } from './themes/DiagramStyles.css';
import { ComponentChildren } from 'preact';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';

/**
 * Props for Link
 */
export type LinkProps<K2, K1> = {
  id: K2;
  label?: string;
  state: State;
  previousState: State;
  isDimmed?: boolean;
  activeId?: string;
  accessibleLabel?: string;
  data: DiagramLink<K2, K1>;
  linkRenderer: (context: LinkRendererContext<K2, K1>) => ComponentChildren;
  supportsSelection: boolean;
};

export const DiagramLinkElement = <K2, K1>({
  id,
  state,
  previousState,
  activeId,
  data,
  linkRenderer,
  accessibleLabel,
  isDimmed,
  supportsSelection
}: LinkProps<K2, K1>) => {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { dimmedItemStyle, linkStyles } = itemStyles;
  const context = {
    state: state,
    previousState: previousState,
    data: data
  };
  return (
    <g
      key={id}
      id={state.focused ? activeId : undefined}
      aria-label={getItemAriaLabel(
        translations,
        supportsSelection,
        state.selected,
        accessibleLabel
      )}
      data-link={id}
      role="img"
      class={classNames([linkStyles, isDimmed && dimmedItemStyle])}>
      {linkRenderer(context)}
    </g>
  );
};
